import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

const STORAGE_KEY = 'selected_currency'

export const useCurrencyStore = defineStore('currency', () => {
  const API_URL = import.meta.env.VITE_API_URL

  const currencies = ref([])
  const loading = ref(false)
  const error = ref(null)

  const selectedCode = ref(localStorage.getItem(STORAGE_KEY) || 'EUR')

  const selectedCurrency = computed(() => {
    return currencies.value.find(c => c.code === selectedCode.value) || null
  })

  const symbol = computed(() => {
    return selectedCurrency.value?.symbol || '€'
  })

  const rate = computed(() => {
    // rates are relative to EUR
    const r = selectedCurrency.value?.rate
    return typeof r === 'number' && !Number.isNaN(r) ? r : 1
  })

  const setCurrency = (code) => {
    selectedCode.value = code
    localStorage.setItem(STORAGE_KEY, code)
  }

  const fetchCurrencies = async () => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${API_URL}/api/currencies`)
      currencies.value = Array.isArray(res.data) ? res.data : []

      if (!currencies.value.some(c => c.code === selectedCode.value)) {
        const fallback = currencies.value.find(c => c.code === 'EUR')?.code || currencies.value[0]?.code || 'EUR'
        setCurrency(fallback)
      }
    } catch (e) {
      error.value = e.response?.data?.message || e.message || 'Failed to load currencies'
      currencies.value = []
    } finally {
      loading.value = false
    }
  }

  const convertFromEur = (amountEur) => {
    const n = Number(amountEur)
    if (Number.isNaN(n)) return 0
    return n * rate.value
  }

  const formatMoney = (amountInSelectedCurrency, digits = 0) => {
    const n = Number(amountInSelectedCurrency)
    if (Number.isNaN(n)) return `${symbol.value}0`
    return `${symbol.value}${n.toFixed(digits)}`
  }

  return {
    currencies,
    loading,
    error,
    selectedCode,
    selectedCurrency,
    symbol,
    rate,
    setCurrency,
    fetchCurrencies,
    convertFromEur,
    formatMoney
  }
})
